import { message } from 'antd'
import axios from 'axios'
import { StatusCodes } from 'http-status-codes'
import { RoutePaths, Session } from 'types/enums'

export const SSO_BASE_URL = process.env.REACT_APP_SSO_BASE_URL
const LMI_PROJECT_ID = process.env.REACT_APP_PROJECT_ID
const LMI_DEFAULT_KEY = process.env.REACT_APP_LMI_DEAFULT_KEY

const setAuthorizationHeader = () => {
  axios.defaults.headers.common.Authorization = `Bearer ${localStorage.getItem(Session.KEY)}`
  axios.defaults.headers['Api-version'] = 'v1'
  axios.interceptors.response.use(
    (res) => res,
    async (err) => {
      const status = err && err.response ? err.response.status : null
      switch (status) {
        case StatusCodes.UNAUTHORIZED:
          message.destroy()
          message.error('Session expired. Please login again')
          window.location.replace('/')
          return Promise.reject(err)
        case StatusCodes.TOO_MANY_REQUESTS:
          message.destroy()
          message.error('Too many request. Please try later')
          return Promise.reject(err)
        default:
          return Promise.reject(err)
      }
    },
  )
}

export default {
  get session() {
    return {
      token: localStorage.getItem(Session.KEY),
      username: localStorage.getItem(Session.USERNAME),
      expiry: localStorage.getItem(Session.EXPIRY),
    }
  },
  /**
   * When this session's token stops being accepted, as a date/time the API can read.
   *
   * The token is opaque, so the API cannot work the expiry out for itself — anything that
   * has to outlive a single request, such as the policies an upload imports, is told when
   * the session ends by whoever makes the request. The stored value is epoch milliseconds;
   * it is sent as UTC without a zone designator, which is the shape the import endpoint
   * was built against.
   *
   * Null when there is no usable expiry — a session started before this was stored
   * correctly, say. Callers leave the value out rather than send a guess: the API caps the
   * association at 24 hours regardless, so an absent expiry is the safe reading.
   */
  get sessionExpiry(): string | null {
    const expiry = Number(localStorage.getItem(Session.EXPIRY))
    if (!Number.isFinite(expiry) || expiry <= 0) return null
    const at = new Date(expiry)
    if (Number.isNaN(at.getTime())) return null
    return at.toISOString().replace(/Z$/, '')
  },
  get encryptedData() {
    return localStorage.getItem(Session.ENCRYPTED_DATA)
  },
  get isLoggedIn() {
    const expiry = localStorage.getItem(Session.EXPIRY)
    if (!expiry || +expiry <= Date.now()) return false
    return !!localStorage.getItem(Session.KEY)
  },
  get callbackLoginUrl() {
    return `${window.location.origin}${RoutePaths.LOGIN}`
  },
  get callbackLogoutUrl() {
    return `${window.location.origin}${RoutePaths.LOGOUT}`
  },
  get ssoLoginUrl() {
    return `${SSO_BASE_URL}/?projectId=${LMI_PROJECT_ID}&callbackUrl=${this.callbackLoginUrl}`
  },
  get ssoLogoutUrl() {
    return `${SSO_BASE_URL}/Signout?callbackUrl=${this.callbackLogoutUrl}`
  },
  get disclaimerAgreed() {
    return localStorage.getItem(Session.DISCLAIMER_AGREED)
  },
  get defaultKey() {
    return LMI_DEFAULT_KEY
  },
  login(token, username, expiry) {
    if (!token) throw new Error('Invalid token')
    localStorage.setItem(Session.KEY, token)
    localStorage.setItem(Session.USERNAME, username)
    localStorage.setItem(Session.EXPIRY, String(Date.now() + expiry * 1000))
    setAuthorizationHeader()
  },
  logout() {
    localStorage.removeItem(Session.KEY)
    localStorage.removeItem(Session.USERNAME)
    localStorage.removeItem(Session.EXPIRY)
    localStorage.removeItem(Session.ENCRYPTED_DATA)
    localStorage.removeItem(Session.LINKGEN_KEY)
    window.location.replace(RoutePaths.ROOT)
  },
  setImpersonateBuyGroupHeader(buyGroupId) {
    axios.defaults.headers['x-impersonate-buy-group'] = buyGroupId
  },
  /**
   * The buy group an admin is impersonating, for callers that bypass axios. It is held on
   * the axios defaults, which fetch knows nothing about, so a fetch-based request has to
   * read it back from here or it runs as the admin's own buy group.
   */
  get impersonateBuyGroup() {
    return axios.defaults.headers['x-impersonate-buy-group']
  },
  removeImpersonateBuyGroupHeader() {
    delete axios.defaults.headers['x-impersonate-buy-group']
  },
  setDisclaimerAgreed() {
    localStorage.setItem(Session.DISCLAIMER_AGREED, 'agreed')
  },
  setAuthorizationHeader,
}
